/**
 * O pedido de consentimento para a análise de uso (Contentsquare, 14/10/2026).
 *
 * Aparece uma vez por aparelho, só quando a análise está configurada neste
 * build (`analiseDisponivel`) e só na área da paciente (`/eu`) — não no meio
 * do cadastro. "Agora não" fica guardado; a análise não carrega.
 *
 * O texto é provisório: precisa da revisão do jurídico antes de a tag ser
 * ligada em produção.
 */
import { useState, useSyncExternalStore, type CSSProperties } from 'react';
import { router } from '../app/router';
import { analiseDisponivel, consentir, lerConsentimento } from './contentsquare';

function useCaminho(): string {
  return useSyncExternalStore(
    (avisar) => router.subscribe(() => avisar()),
    () => router.state.location.pathname,
  );
}

export function AvisoDeAnalise() {
  const caminho = useCaminho();
  const [decidido, setDecidido] = useState(() => lerConsentimento() !== null);
  if (!analiseDisponivel || decidido || !(caminho === '/eu' || caminho.startsWith('/eu/'))) return null;

  const decidir = (sim: boolean) => {
    consentir(sim);
    setDecidido(true);
  };

  return (
    <div role="dialog" aria-label="Análise de uso" style={estilos.caixa}>
      <p style={estilos.titulo}>Ajude a melhorar o app</p>
      <p style={estilos.texto}>
        Com a sua permissão, usamos uma ferramenta de análise (Contentsquare) para entender como o app é usado — onde as pessoas tocam e onde se perdem. O que você escreve e os textos das
        telas ficam ocultos.
      </p>
      <div style={{ display: 'flex', gap: 10 }}>
        <button type="button" onClick={() => decidir(false)} style={{ ...estilos.botao, ...estilos.secundario }}>
          Agora não
        </button>
        <button type="button" onClick={() => decidir(true)} style={{ ...estilos.botao, ...estilos.primario }}>
          Permitir
        </button>
      </div>
    </div>
  );
}

const estilos: Record<string, CSSProperties> = {
  caixa: {
    position: 'fixed',
    left: 12,
    right: 12,
    bottom: 'calc(env(safe-area-inset-bottom, 0px) + 96px)',
    zIndex: 950,
    maxWidth: 480,
    margin: '0 auto',
    padding: 16,
    borderRadius: 20,
    background: '#FFFFFF',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.16)',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  titulo: { margin: 0, fontFamily: 'var(--eva-font-titulo)', fontSize: 15, fontWeight: 700, color: '#111827' },
  texto: { margin: 0, fontSize: 13, lineHeight: '19px', color: '#4A5565' },
  botao: { flex: 1, height: 40, borderRadius: 999, border: 'none', fontFamily: 'var(--eva-font-titulo)', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  primario: { background: 'var(--eva-brand-accent)', color: '#FFFFFF' },
  secundario: { background: 'var(--eva-brand-tom-claro)', color: 'var(--eva-brand-accent-text)' },
};
